import React, { useState } from "react";
import { Grid, Typography, Box, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import postsServices from "../../../services/api/service/postsService";
import RemoveIcon from "./../../../assets/images/icons/remove-icon.svg";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, store } from "../../../services/redux/store";
import {
  clearCourseFieldValues,
  setCgpaMode,
  setHorizontalSteps,
} from "../../../services/redux/slices/stepperDynamicFormSlice";
import ModalPopup from "../../ui/ModalPopup";
import { PrimaryButton } from "../../ui/Button";
import SummarySectionHeader from "../../applicantDetailsTabs/SummarySectionHeader";

const CourseDetailsSection = ({
  fields,
  renderField,
  formikRef,
  horizontalSteps,
  activeVerticalIndex,
  activeHorizontalIndex,
  trackingId,
}: any) => {
  const dispatch = useDispatch<AppDispatch>();
  // Confirmation modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingRemoveIds, setPendingRemoveIds] = useState<number[] | null>(
    null
  );

  const courseTypeOptions =
    fields.find((f: any) =>     f.name?.includes("course") && f.name?.endsWith("Type"))?.options?.options ?? [];

  const scoreTypeOptions =
    fields.find((f: any) => f.name?.includes("ScoreType"))?.options?.options ??
    [];

  // Find the education gap field
  const gapField = fields.find((f: any) => f.name === "educationGap");

  // Get the current vertical step object
  const currentVerticalStep =
    horizontalSteps?.[activeHorizontalIndex]?.verticalSteps?.[
      activeVerticalIndex
    ];

  // Get the vertical step id
  const verticalStepId = currentVerticalStep?.id;

  const addCourseFields = async () => {
    try {
      // Find all course numbers in use
      const courseNums = fields
        .map((f: any) => {
          const match = f.name && f.name.match(/^course(\d+)/);
          return match ? parseInt(match[1], 10) : null;
        })
        .filter((n: number | null) => n !== null);
      // Find the next available course number (smallest missing positive integer)
      let nextCourseNum = 1;
      while (courseNums.includes(nextCourseNum)) {
        nextCourseNum++;
      }

      const apiFields = [
        {
          label: `Course Type`,
          name: `course${nextCourseNum}Type`,
          type: "dropdown",
          required: true,
          options: {
            options: courseTypeOptions,
          },
        },
        // {
        //   label: `Score ${nextCourseNum} Type`,
        //   name: `course${nextCourseNum}ScoreType`,
        //   type: "toggle",
        //   required: true,
        //   options: {
        //     options: scoreTypeOptions,
        //   },
        // },
        {
          label: `Marks Obtained`,
          name: `course${nextCourseNum}Percentage`,
          type: "number",
          required: true,
        },
        {
          label: `Year of Passing`,
          name: `course${nextCourseNum}Year`,
          type: "dropdown",
          required: true,
        },
      ];
      const resp = await postsServices.addPost(
        "api/v1/product/store-user-fields",
        {
          vertical_step_id: verticalStepId,
          fields: apiFields,
          trackingId: trackingId,
        }
      );
      if (resp?.data?.status === "success" && Array.isArray(resp?.data?.data)) {
        const newFields = resp.data.data.map((f: any) => ({
          ...f,
          options: f?.options ? f?.options : null,
        }));

        // --- PUSH new fields into the correct vertical step (id: 9, Loan Details) in applicant_loan horizontal step in Redux state ---
        const state = store.getState();
        if (
          state &&
          state.stepper &&
          Array.isArray(state.stepper.horizontalSteps)
        ) {
          const horizontalSteps = [...state.stepper.horizontalSteps];

          const applicantLoanStepIdx = horizontalSteps.findIndex(
            (step: any) => step.stepKey === "applicant_loan"
          );

          if (applicantLoanStepIdx !== -1) {
            const applicantLoanStep = {
              ...horizontalSteps[applicantLoanStepIdx],
            };
            const verticalSteps = [...applicantLoanStep.verticalSteps];
            // Instead of hardcoding id === 9, use the current vertical step (activeVerticalIndex)
            const targetVerticalIdx = activeVerticalIndex;
            if (
              typeof targetVerticalIdx === "number" &&
              targetVerticalIdx >= 0 &&
              targetVerticalIdx < verticalSteps.length
            ) {
              const targetStep = { ...verticalSteps[targetVerticalIdx] };
              targetStep.fields = [...targetStep.fields, ...newFields];
              verticalSteps[targetVerticalIdx] = targetStep;
              applicantLoanStep.verticalSteps = verticalSteps;
              horizontalSteps[applicantLoanStepIdx] = applicantLoanStep;
              // Dispatch redux update using setHorizontalSteps action creator
              dispatch(setHorizontalSteps(horizontalSteps));
            }
          }
        }

        // Set Formik default values for the new fields
        if (formikRef?.current) {
          const newFieldDefaults: Record<string, any> = {};
          newFields.forEach((field: any) => {
            newFieldDefaults[field.name] = "";
            formikRef.current.setFieldValue(field.name, "", false);
            formikRef.current.setFieldTouched(field.name, false, false);
            formikRef.current.setFieldError(field.name, undefined);
          });
          dispatch(clearCourseFieldValues(Object.keys(newFieldDefaults)));
        }

        // Set Redux cgpaModes for the new course to true (CGPA)
        dispatch(
          setCgpaMode({
            courseNum: String(nextCourseNum),
            mode: true,
          })
        );
      }
    } catch (err) {
      // handle error
    }
  };

  const removeCourseFields = async (fieldIds: number[]) => {
    try {
      const resp = await postsServices.addPost(
        "api/v1/product/delete-user-fields",
        {
          form_field_ids: fieldIds,
          trackingId: trackingId
        }
      );

      if (resp.data?.status === "success") {
        // Remove from Redux: Remove fields from horizontalSteps (vertical step id: 9 in applicant_loan)
        const state = store.getState();
        if (
          state &&
          state.stepper &&
          Array.isArray(state.stepper.horizontalSteps)
        ) {
          const horizontalSteps = [...state.stepper.horizontalSteps];
          const applicantLoanStepIdx = horizontalSteps.findIndex(
            (step: any) => step.stepKey === "applicant_loan"
          );
          if (applicantLoanStepIdx !== -1) {
            const applicantLoanStep = {
              ...horizontalSteps[applicantLoanStepIdx],
            };
            const verticalSteps = [...applicantLoanStep.verticalSteps];
            // Use the current vertical step (activeVerticalIndex) instead of hardcoded id === 9
            const targetVerticalIdx = activeVerticalIndex;
            if (
              typeof targetVerticalIdx === "number" &&
              targetVerticalIdx >= 0 &&
              targetVerticalIdx < verticalSteps.length
            ) {
              const targetStep = { ...verticalSteps[targetVerticalIdx] };
              targetStep.fields = targetStep.fields.filter(
                (field: any) => !fieldIds.includes(field.id)
              );
              verticalSteps[targetVerticalIdx] = targetStep;
              applicantLoanStep.verticalSteps = verticalSteps;
              horizontalSteps[applicantLoanStepIdx] = applicantLoanStep;
              // Dispatch redux update
              dispatch(setHorizontalSteps(horizontalSteps));
            }
          }
        }

        // Find field names to clear
        const fieldNamesToClear: any[] = [];
        fields.forEach((field: any) => {
          if (fieldIds.includes(field.id)) {
            fieldNamesToClear.push(field.name);
          }
        });

        // Clear values from Redux
        dispatch(clearCourseFieldValues(fieldNamesToClear));

        // If you use Formik for values, clear those too
        if (formikRef?.current) {
          fieldNamesToClear.forEach((name) => {
            formikRef.current.setFieldValue(name, "");
          });
        }
      }
    } catch (err) {
      console.error("Error deleting course fields", err);
    }
  };

  // Handler for remove button click
  const handleRemoveClick = (ids: number[]) => {
    setPendingRemoveIds(ids);
    setConfirmOpen(true);
  };

  // Handler for confirm in modal
  const handleConfirmRemove = () => {
    if (pendingRemoveIds !== null) {
      removeCourseFields(pendingRemoveIds);
    }
    setConfirmOpen(false);
    setPendingRemoveIds(null);
  };

  // Handler for cancel in modal
  const handleCancelRemove = () => {
    setConfirmOpen(false);
    setPendingRemoveIds(null);
  };

  return (
    <Grid container mx={2} mt={2}>
      {/* Education Gap */}
      {gapField && (
        <Grid container sx={{ mb: 2 }}>
          <Grid item xs={4}>
            {renderField(gapField)}
          </Grid>
        </Grid>
      )}
      {/* Note */}
      <Grid
        item
        xs={12}
        sx={{ mb: 1, p: 1 }}
        style={{ backgroundColor: "#EBF9F9", borderRadius: "8px" }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 400, fontSize: "15px", color: "#5E596C" }}
        >
          <span style={{ fontWeight: 700 }}>Note:</span> Please add your course
          marks in percentage format."(e.g., 85%)"
        </Typography>
      </Grid>

      {/* Dynamically render all courses: group by course number */}
      {(() => {
        // Exclude educationGap and any non-course fields
        const courseFields = fields.filter((f: any) =>
          /^course\d+/.test(f.name)
        );

        // Group by course number (e.g. course1, course2, ...)
        const courseGroups: { [courseNum: string]: any[] } = {};
        courseFields.forEach((f: any) => {
          const match = f.name.match(/^course(\d+)/);
          if (match) {
            const num = match[1];
            if (!courseGroups[num]) courseGroups[num] = [];
            courseGroups[num].push(f);
          }
        });
        // Sort course numbers
        const sortedCourseNums = Object.keys(courseGroups).sort(
          (a, b) => Number(a) - Number(b)
        );
        return sortedCourseNums.map((num, idx) => {
          const group = courseGroups[num].sort(
            (a, b) => a.fieldOrder - b.fieldOrder
          );
          // Find a label for the group (e.g. from Type field or fallback)
          const title = `Qualification ${num}`;
          // Exclude non-visible/meta fields from the main row
          const visibleFields = group.filter(
            (f: any) =>
              !/ScoreType$/i.test(f.name) &&
              !/TypeId$/i.test(f.name) &&
              f.type !== "hidden"
          );

          // Collect all field ids for remove
          const fieldIds = group.map((f: any) => f.id);
          // Show remove only for extra courses (isRemoved true on any field)
          const isExtra = group.some((f: any) => f.isRemoved);
          return (
            <React.Fragment key={num}>
              <Grid item xs={12} sx={{ mb: 1 }}>
                <SummarySectionHeader title={title} />
              </Grid>
              <Grid
                container
                className="course-details-section-row"
                alignItems={"flex-end"}
                spacing={2}
                sx={{ mb: 2 }}
              >
                {visibleFields.map((f: any) => (
                  <React.Fragment key={f.id}>
                    {renderField && renderField(f)}
                  </React.Fragment>
                ))}
              </Grid>
              {isExtra && (
                <Grid item xs={12} sx={{ textAlign: "left" }}>
                  <Box
                    sx={{ display: "inline-flex", cursor: "pointer" }}
                    onClick={() => handleRemoveClick(fieldIds)}
                  >
                    <img src={RemoveIcon} alt="arrow-narrow-right" />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        ml: 1,
                        fontWeight: 600,
                        color: "#FF4C51",
                        fontSize: "13px",
                      }}
                    >
                      Remove
                    </Typography>
                  </Box>
                </Grid>
              )}
            </React.Fragment>
          );
        });
      })()}

      {/* Add More Button */}
      {courseTypeOptions?.length > 0 &&
        (() => {
          // Count current course groups
          const courseFields = fields.filter((f: any) =>
            /^course\d+/.test(f.name)
          );
          const courseNums = new Set();
          courseFields.forEach((f: any) => {
            const match = f.name.match(/^course(\d+)/);
            if (match) courseNums.add(match[1]);
          });
          const disableAdd = courseNums.size >= courseTypeOptions.length;
          return (
            <Grid item xs={12} sx={{ textAlign: "right" }}>
              <Box sx={{ mt: 2 }}>
                <Button
                  sx={{
                    textTransform: "none",
                    fontFamily: "Jost, sans-serif",
                    fontWeight: 600,
                    fontSize: "14px",
                    lineHeight: "100%",
                    letterSpacing: "0%",
                    color: "#33c2c4",
                    borderColor: "#33c2c4",
                    "&:hover": {
                      backgroundColor: "transparent",
                      borderColor: "#33c2c4",
                      color: "#33c2c4",
                    },
                  }}
                  onClick={addCourseFields}
                  disabled={disableAdd}
                >
                  <AddIcon sx={{ mr: 1, fontSize: "20px" }} />
                  Add More Course
                </Button>
              </Box>
            </Grid>
          );
        })()}

      {/* Confirmation Modal */}
      <ModalPopup
        isOpen={confirmOpen}
        onClose={handleCancelRemove}
        subTitle="Remove Course"
      >
        <Box sx={{ p: 2 }}>
          <Typography>
            Are you sure you want to remove this course? This action cannot be
            undone.
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 2 }}
          >
            <Button
              onClick={handleCancelRemove}
              color="primary"
              size="small"
              variant="outlined"
            >
              Cancel
            </Button>
            <PrimaryButton onClick={handleConfirmRemove} variant="contained">
              Confirm
            </PrimaryButton>
          </Box>
        </Box>
      </ModalPopup>
    </Grid>
  );
};

export default CourseDetailsSection;
